import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail } from 'lucide-react';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'The Sanctuary', href: '/#about' },
    { name: 'Founders', href: '/#founders' },
    { name: 'Rooms', href: '/#rooms' },
    { name: 'Journal', href: '/#activities' },
    { name: 'Stories', href: '/#testimonials' },
    { name: 'Around', href: '/#sightseeing' },
    { name: 'Contact', href: '/#contact' },
  ];

  const scrollToSection = (href) => {
    setIsMobileMenuOpen(false);
    if (href.startsWith('/#')) {
      const sectionId = href.substring(2);
      if (location.pathname !== '/') {
        window.location.href = href;
      } else {
        const element = document.getElementById(sectionId);
        if (element) element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? 'bg-[#0B0F0E]/85 backdrop-blur-xl border-b border-[#D4AF37]/15 py-3'
            : 'bg-transparent py-5'
        }`}
        data-testid="header"
      >
        <div className="max-w-[1480px] mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center" data-testid="logo-link">
              <img
                src="https://customer-assets.emergentagent.com/job_b94b95c1-d586-44dc-829b-05989d33f0ec/artifacts/jj6t2qbc_tusker-off-whiteAsset.png"
                alt="Tuskermark Safari Retreat"
                className="h-8 md:h-12 w-auto object-contain"
              />
            </Link>

            <nav className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                link.href.startsWith('/#') ? (
                  <button
                    key={link.name}
                    onClick={() => scrollToSection(link.href)}
                    className="link-underline text-[12px] tracking-[0.2em] uppercase font-medium text-[#E8E3D9]/80 hover:text-[#D4AF37] transition-colors"
                    data-testid={`nav-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    {link.name}
                  </button>
                ) : (
                  <Link
                    key={link.name}
                    to={link.href}
                    className="link-underline text-[12px] tracking-[0.2em] uppercase font-medium text-[#E8E3D9]/80 hover:text-[#D4AF37] transition-colors"
                    data-testid={`nav-${link.name.toLowerCase()}`}
                  >
                    {link.name}
                  </Link>
                )
              ))}
            </nav>

            <div className="hidden lg:flex items-center gap-6">
              <a
                href="tel:+918310838799"
                className="flex items-center gap-2 text-[12px] tracking-wider text-[#C4BFB1] hover:text-[#D4AF37] transition-colors"
                data-testid="header-phone"
              >
                <Phone size={14} />
                +91 83108 38799
              </a>
              <button
                onClick={() => scrollToSection('/#rooms')}
                className="px-5 py-2.5 bg-[#D4AF37] text-[#0B0F0E] text-[11px] font-semibold tracking-[0.2em] uppercase hover:bg-[#E5C766] transition-all"
                data-testid="header-book-btn"
              >
                Reserve
              </button>
            </div>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 text-[#E8E3D9]"
              data-testid="mobile-menu-btn"
              aria-label="Menu"
            >
              {isMobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu */}
      <div className={`mobile-menu lg:hidden ${isMobileMenuOpen ? 'open' : ''}`}>
        <div className="p-8 h-full flex flex-col">
          <div className="flex justify-between items-center mb-12">
            <img
              src="https://customer-assets.emergentagent.com/job_b94b95c1-d586-44dc-829b-05989d33f0ec/artifacts/jj6t2qbc_tusker-off-whiteAsset.png"
              alt="Tuskermark"
              className="h-8 w-auto object-contain"
            />
            <button onClick={() => setIsMobileMenuOpen(false)} data-testid="close-mobile-menu" aria-label="Close">
              <X size={24} className="text-[#E8E3D9]" />
            </button>
          </div>
          <nav className="flex flex-col gap-6">
            {navLinks.map((link) => (
              link.href.startsWith('/#') ? (
                <button
                  key={link.name}
                  onClick={() => scrollToSection(link.href)}
                  className="text-left font-serif text-3xl text-[#E8E3D9] hover:text-[#D4AF37] transition-colors py-1"
                  data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                >
                  {link.name}
                </button>
              ) : (
                <Link
                  key={link.name}
                  to={link.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-left font-serif text-3xl text-[#E8E3D9] hover:text-[#D4AF37] transition-colors py-1"
                  data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </Link>
              )
            ))}
          </nav>
          <div className="mt-auto pt-8 border-t border-[#D4AF37]/20">
            <a href="tel:+918310838799" className="flex items-center gap-3 text-[#C4BFB1] mb-3">
              <Phone size={16} className="text-[#D4AF37]" />
              +91 83108 38799
            </a>
            <a href="mailto:info@tuskermark.com" className="flex items-center gap-3 text-[#C4BFB1] mb-6 text-sm break-all">
              <Mail size={16} className="text-[#D4AF37] flex-shrink-0" />
              info@tuskermark.com
            </a>
            <button
              onClick={() => scrollToSection('/#rooms')}
              className="w-full btn-brass justify-center"
              data-testid="mobile-book-btn"
            >
              Reserve a stay
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
